import { ApplicationConfig } from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, RouteReuseStrategy } from '@angular/router';
import { NeverReuseRouteStrategy } from './guards/never-reuse-route.strategy';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideZonelessChangeDetection } from '@angular/core';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Material from '@primeng/themes/material';
import { provideStore } from '@ngxs/store';
import { ConfirmationService } from 'primeng/api';

import { routes } from './app.routes';
import { tratonPreset } from './primeng-design-tokens/traton-preset';
import { ThemeService } from './services/theme.service';
import { LoaderInterceptor } from './interceptors/loader.interceptor';
import { AuthInterceptor } from './interceptors/auth.interceptor';

import { TpcState } from './store/tpc/tpc.state';
import { FpcsState } from './store/fpcs/fpcs.state';
import { GitLabScriptsState } from './store/gitLabScripts/gitLabScripts.state';
import { TestLevelsState } from './store/testLevels/testLevels.state';
import { EcuState } from './store/ecu/ecu.state';
import { SopState } from './store/sop/sop.state';
import { TestCasesState } from './store/testCases/testCases.state';
import { TestSuitesState } from './store/testSuites/testSuites.state';
import { OrganizationalGroupsState } from './store/organizationalGroups/organizationalGroups.state';
import { OffboardComponentsState } from './store/offboardComponents/offboardComponents.state';
import { ProductPropertiesState } from './store/productPropeties/productProperties.state';
import { TestAnalysisState } from './store/testAnalysis/testAnalysis.state';
import { SpecificationCoverageState } from './store/specificationCoverage/specificationCoverage.state';
import { EeseCoverageState } from './store/eeseCoverage/eeseCoverage.state';
import { SoftwareVersionCoverageState } from './store/softwareVersionCoverage/softwareVersionCoverage.state';
import { TestSpecificationsState } from './store/testSpecifications/testSpecifications.state';
import { TestObjectsState } from './store/testObjects/testObjects.state';
import { UserFunctionsState } from './store/user-functions/user-functions.state';
import { VehicleFunctionsState } from './store/vehicleFunctions/vehicleFunctions.state';
import { FunctionalGroupsState } from './store/functionalGroups/functionalGroups.state';
import { TemplatesState } from './store/templates/templates.state';
import { TagsState } from './store/tags/tags.state';
import { EeseRequirementsState } from './store/eeseRequirements/eeseRequirements.state';
import { ArpyVehicleFunctionsState } from './store/arpyVehicleFunctions/arpyVehicleFunctions.state';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
    ),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideZonelessChangeDetection(),
    providePrimeNG({
      ripple: false,
      theme: {
        // definePreset unifies all args to the base preset's (Material's) token type in
        // themes v21; tratonPreset is a looser override object, so cast it. definePreset
        // deep-merges tokens at runtime regardless of type, so behaviour is unchanged.
        preset: definePreset(Material, tratonPreset as any, {
          //application override
        }),
        options: { darkModeSelector: '.theme-dark' }
      }
    }),
    provideStore([
      TpcState,
      FpcsState,
      GitLabScriptsState,
      TestLevelsState,
      EcuState,
      SopState,
      TestCasesState,
      TestSuitesState,
      OrganizationalGroupsState,
      OffboardComponentsState,
      ProductPropertiesState,
      TestAnalysisState,
      SpecificationCoverageState,
      EeseCoverageState,
      SoftwareVersionCoverageState,
      TestSpecificationsState,
      TestObjectsState,
      UserFunctionsState,
      VehicleFunctionsState,
      FunctionalGroupsState,
      TemplatesState,
      TagsState,
      EeseRequirementsState,
      ArpyVehicleFunctionsState
    ]),
    { provide: RouteReuseStrategy, useClass: NeverReuseRouteStrategy },
    ConfirmationService,
    ThemeService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    }
  ]
};
